import {
	DateString,
	TableView,
	TableViewNoResultConfig,
	VirtualTableViewColumnDefinition
} from "client/Components";
import { SortingFor } from "client/Data/sorting";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useUserTimezone
} from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../../DocumentName";
import { GridItem } from "../../WebDav/WebDavOverview";
import { checkIsDocumentDisabled } from "./WebDavPickerOverview";
import { TimeZone } from "client/DateAndTime/timezones";
import { ItemPickerMode } from "../ItemPicker";

export type WebDavPickerTableViewHandle = {
	resetScrollPosition: () => void;
};

export type WebDavPickerTableViewProps = {
	isBusy: boolean;
	sortingFor: SortingFor<GridItem>;
	multiSelection: MultiSelectableOptions | undefined;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	items: readonly GridItem[];
	onOpenItem: (item: GridItem) => void;
	mode: ItemPickerMode;
	hasSearch: boolean;
};

export const WebDavPickerTableView = forwardRef(
	(props: WebDavPickerTableViewProps, ref: Ref<WebDavPickerTableViewHandle>): ReactElement => {
		const {
			isBusy,
			sortingFor,
			multiSelection,
			selectionFor,
			items,
			onOpenItem,
			mode,
			hasSearch
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const columns: readonly VirtualTableViewColumnDefinition[] = useColumnDefinitions(
			sortingFor
		);

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				if (isBusy) {
					return undefined;
				}

				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch, isBusy]
		);

		return (
			<TableView
				isVirtual
				hasNoBottomDelimiter
				columns={columns}
				items={items}
				height="fill"
				itemKey="id"
				trackingId="webdav-grid"
				multiSelectable={multiSelection}
				ref={tableRef}
				shouldHideDisabledCheckboxes
				renderAfterItems={
					<TableView.LoadMoreIndicator
						isLoadingData={isBusy}
						hasMoreItems={false}
						onRequestMoreItems={() => { /* NOOP */ }}
					/>
				}
				noResultConfig={noResultConfig}
			>
				{(x): ReactElement => (
					<TableView.Row
						trackingId={x.id}
						selectable={selectionFor(x)}
						isDisabled={checkIsDocumentDisabled(x, mode)}
					>
						<TableView.Cell>
							<DocumentName
								name={x.name}
								documentKind={x.kind}
								mode={{
									kind: DocumentNameKind.Default,
									hasBookmark: x.hasBookmark
								}}
								onClick={
									!checkIsDocumentDisabled(x, mode)
										? () => onOpenItem(x)
										: undefined
								}
							/>
						</TableView.Cell>
						<TableView.Cell>
							{x.creationDate && (
								<DateString
									value={x.creationDate}
									format="RelativeTimestamp"
									timezone={userTimezone}
								/>
							)}
						</TableView.Cell>
						<TableView.Cell>
							{x.modifiedDate && (
								<DateString
									value={x.modifiedDate}
									format="RelativeTimestamp"
									timezone={userTimezone}
								/>
							)}
						</TableView.Cell>
						<TableView.Cell>{x.extension}</TableView.Cell>
						<TableView.Cell>
							{getDisplayFriendlySize(x.size)}
						</TableView.Cell>
					</TableView.Row>
				)}
			</TableView>
		);
	}
);
WebDavPickerTableView.displayName = "WebDavPickerTableView";

function useColumnDefinitions(
	sortingFor: SortingFor<GridItem>
): readonly VirtualTableViewColumnDefinition[] {
	return useMemo(
		(): readonly VirtualTableViewColumnDefinition[] => {
			return [
				{
					title: localize("_Name"),
					sortable: sortingFor("nameSorting"),
					trackingId: "name",
					width: 400
				},
				{
					title: localize("_DateCreated"),
					sortable: sortingFor("creationDateSorting"),
					trackingId: "created-on",
					width: 150
				},
				{
					title: localize("_DateModified"),
					sortable: sortingFor("modifiedDateSorting"),
					trackingId: "modified-on",
					width: 150
				},
				{
					title: localize("_Extension"),
					sortable: sortingFor("extension"),
					trackingId: "extension",
					width: 150
				},
				{
					title: localize("_FileSize"),
					sortable: sortingFor("size"),
					trackingId: "size",
					width: 150
				}
			];
		},
		[sortingFor]
	);
}
